<script setup lang="ts">
import { computed, type PropType, ref, toRef } from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import { useDragAndDrop } from '@formkit/drag-and-drop/vue';
import { animations } from '@formkit/drag-and-drop';
import { ButtonType, CustomButton } from '@/components';
import { type MatrixColumn, FormFieldChoice } from '@/models';
import { cloneDeep } from 'lodash';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  column: {
    type: Object as PropType<MatrixColumn>
  }
});
const initialColumn = toRef<MatrixColumn>(props.column!);
const localColumn = ref<MatrixColumn>(
  cloneDeep(props.column!)
);
const [parent, choices] = useDragAndDrop(localColumn.value.options!.choices!, {
  plugins: [animations()]
});

const emits = defineEmits(['onClose', 'onSave']);

const closeDialog = () => {
  emits('onClose');
};

const onTab = (event: KeyboardEvent, choice: FormFieldChoice) => {
  const choiceIndex = localColumn.value.options!.choices!.findIndex(
    fChoice => (fChoice.id = choice.id)
  );
  if (
    choiceIndex > -1 &&
    choiceIndex == localColumn.value.options!.choices!.length - 1
  ) {
    event.preventDefault();
    localColumn.value.options!.choices!.push({
      id:
        (parseFloat(
          `${localColumn.value.options!.choices![choiceIndex].id}`
        ) as number) + 1,
      label: '',
      value: ''
    });
  }
};

const addNewChoice = () => {
  localColumn.value.options!.choices!.push({
    id:
      (parseFloat(
        `${
          localColumn.value.options!.choices![
            localColumn.value.options!.choices!.length - 1
          ].id
        }`
      ) as number) + 1,
    label: '',
    value: ''
  });
};

const onDeleteChoice = (index: number) => {
  if (localColumn.value.options!.choices!.length > 1) {
    localColumn.value.options?.choices!.splice(index, 1);
  }
};

const isPristine = computed(
  () =>
    JSON.stringify(initialColumn.value.options?.choices) !==
      JSON.stringify(localColumn.value.options?.choices) &&
    localColumn.value.options?.choices!.every(
      choice => choice.label != '' && choice.value != ''
    )
);

const saveConfig = () => {
  initialColumn.value.options!.choices = choices.value as FormFieldChoice[];
  closeDialog();
};
</script>

<template>
  <div class="dialog-root">
    <Dialog :visible="visible"
      header="Edit choices list"
      modal
      class="dialog"
      append-to="self"
      @update:visible="closeDialog">
      <div class="dialog-content">
        <div class="warning-block">
          <h2>
            <FontAwesomeIcon :icon="[`far`, `exclamation-triangle`]"
              class="exclamation-icon"/>
            Risk of data loss
          </h2>
          <p>
            When adding new choices always add them at the
            <span>bottom of the list.</span>
            Otherwise, you will affect the data that has already been collected.
          </p>
        </div>

        <div class="choices-title">Choices</div>
        <div class="choices-list" ref="parent">
          <div v-for="(choice, index) in choices"
            :key="choice.id"
            class="choice-list-item">
            <FontAwesomeIcon :icon="[`fas`, `grip-vertical`]"
              class="icon grip"/>
            <InputText v-model="choice.label"
              type="text"
              class="choice-label-input"/>
            <InputText v-model="choice.value as string"
              type="text"
              @keydown.tab="(event) => onTab(event, choice)"
              class="choice-value-input"/>
            <FontAwesomeIcon :icon="[`fal`, `trash`]"
              class="icon trash"
              @click="onDeleteChoice(index)"/>
          </div>
          <div class="add-choice">
            <CustomButton text="Add choice"
              :icon="{ style: 'fas', icon: 'plus' }"
              @onClick="addNewChoice"
              :type="ButtonType.OUTLINED"/>
          </div>
        </div>
      </div>
      <div class="footer">
        <div class="empty"></div>
        <div class="footer-buttons">
          <CustomButton text="Cancel"
            @onClick="closeDialog"
            :margin-left="16"
            :type="ButtonType.OUTLINED"/>
          <CustomButton text="Save"
            @onClick="saveConfig"
            :disabled="!isPristine"
            :margin-left="16"
            :type="ButtonType.POSITIVE"/>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
@import "../../../../assets/dialog";
:deep(.p-dialog) {
  max-height: 80vh;
}
:deep(.p-dialog-header) {
  padding: unset;
  padding: 40px 40px 0px 40px;
}
:deep(.p-dialog-content) {
  padding: unset;
}
.dialog-content {
  width: 600px;
  padding: 8px 40px 40px 40px;
  margin-bottom: 40px;
}
.warning-block {
  margin-bottom: 40px;
  padding: 12px;
  border: 1px solid #ea3546;
  border-radius: 4px;
  background-color: #fef5f6;
  color: #5c0a11;
  h2 {
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
    margin: 0 0 4px 0;
    .exclamation-icon {
      color: #ea3546;
      line-height: 14px;
      font-size: 16px;
      margin-right: 8px;
    }
  }
  p {
    margin: 0;
  }
}
.choice-list-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .choice-label-input {
    flex: 1;
    margin-left: 16px;
  }
  .choice-value-input {
    width: 100px;
    margin-left: 16px;
    margin-right: 16px;
  }
}
.icon {
  cursor: pointer;
}
.grip {
  cursor: grab;
}
.add-choice {
  margin-top: 24px;
  display: flex;
  justify-content: center;
}
.footer {
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #ced3d9;
  background-color: #f5f7f7;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 9999;

  .footer-buttons {
    display: flex;
  }
}
</style>
